import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const axiosApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Flag to prevent infinite refresh loops
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const clearUserTokens = () => {
  localStorage.removeItem("user_access_token");
  localStorage.removeItem("user_refresh_token");
  localStorage.removeItem("user_token_expires_at");
};

axiosApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("user_access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh on 401 errors, and not on refresh-token or sign-in requests
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("refresh-token") &&
      !originalRequest.url?.includes("sign-in")
    ) {
      // Check if this is a disabled account error — don't refresh, just redirect
      const errorMessage = (error.response?.data as any)?.message || "";
      if (
        errorMessage.toLowerCase().includes("disabled") ||
        errorMessage === "Your account has been disabled. Please contact the administrator."
      ) {
        clearUserTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem("user_refresh_token");

      if (!refreshToken) {
        clearUserTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosApi(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/dealer/refresh-token`,
          { refresh_token: refreshToken }
        );

        const { access_token, refresh_token: newRefreshToken, expires_in } = response.data;

        localStorage.setItem("user_access_token", access_token);
        localStorage.setItem("user_refresh_token", newRefreshToken);
        localStorage.setItem("user_token_expires_at", String(Date.now() + expires_in * 1000));

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        processQueue(null, access_token);

        return axiosApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearUserTokens();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For non-401 errors or if refresh already failed.
    // Don't redirect on sign-in or refresh-token failures — let the caller handle those.
    if (
      error.response?.status === 401 &&
      !originalRequest.url?.includes("sign-in") &&
      !originalRequest.url?.includes("refresh-token")
    ) {
      clearUserTokens();
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

export async function get(url: string, config = {}) {
  return await axiosApi
    .get(url, { ...config })
    .then((response) => response.data);
}

export async function post(url: string, data: {}, config = {}) {
  return await axiosApi
    .post(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function put(url: string, data: {}, config = {}) {
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function patch(url: string, data: {}, config = {}) {
  return axiosApi
    .patch(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function del(url: string, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then((response) => response.data);
}
